// src/pages/SuperAdminPage.js
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import BrandFooter from '../components/BrandFooter';
import QRModal from '../components/QRModal';
import PlanSelector from '../components/admin/PlanSelector'; // eslint-disable-line no-unused-vars -- used in JSX

export default function SuperAdminPage({ token, user, onLogout, onImpersonate }) {
  const [restaurants, setRestaurants] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [message, setMessage] = useState('');
  const [qrRestaurant, setQrRestaurant] = useState(null);
  const [planRestaurant, setPlanRestaurant] = useState(null); // eslint-disable-line no-unused-vars -- used in JSX

  const api = axios.create({ headers: { Authorization: `Bearer ${token}` } });

  const showMsg = (text) => {
    setMessage(text);
    setTimeout(() => setMessage(''), 3000);
  };

  const fetchRestaurants = async () => {
    try {
      const { data } = await api.get('/api/auth/restaurants');
      setRestaurants(data);
    } catch {
      showMsg('❌ Ошибка загрузки');
    }
  };

  useEffect(() => {
    fetchRestaurants();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDelete = async (r) => {
    if (!window.confirm(`Удалить «${r.name}»? Удалится всё меню!`)) return;
    try {
      await api.delete(`/api/auth/restaurants/${r.id}`);
      showMsg('✅ Удалено');
      fetchRestaurants();
    } catch {
      showMsg('❌ Ошибка удаления');
    }
  };

  const handleExtendTrial = async (r) => {
    try {
      await api.post(`/api/auth/restaurants/${r.id}/extend-trial`);
      showMsg('✅ Триал продлён на 14 дней');
      fetchRestaurants();
    } catch (err) {
      showMsg('❌ ' + (err.response?.data?.error || 'Ошибка'));
    }
  };

  const handleActivate = async (r) => {
    try {
      await api.post(`/api/auth/restaurants/${r.id}/activate`);
      showMsg('✅ Ресторан активирован');
      fetchRestaurants();
    } catch (err) {
      showMsg('❌ ' + (err.response?.data?.error || 'Ошибка'));
    }
  };

  const handleReturnToTrial = async (r) => {
    if (!window.confirm(`Вернуть «${r.name}» в триал на 14 дней?`)) return;
    try {
      await api.post(`/api/auth/restaurants/${r.id}/set-status`, { status: 'trial' });
      await api.post(`/api/auth/restaurants/${r.id}/extend-trial`);
      showMsg('✅ Возвращён в триал на 14 дней');
      fetchRestaurants();
    } catch (err) {
      showMsg('❌ ' + (err.response?.data?.error || 'Ошибка'));
    }
  };

  const handleToggleFrozen = async (r) => {
    try {
      const newStatus = r.status === 'frozen' ? (r.activated_at ? 'active' : 'trial') : 'frozen';
      await api.post(`/api/auth/restaurants/${r.id}/set-status`, { status: newStatus });
      showMsg('✅ Статус изменён');
      fetchRestaurants();
    } catch (err) {
      showMsg('❌ ' + (err.response?.data?.error || 'Ошибка'));
    }
  };

  const handleImpersonate = async (r) => {
    try {
      const { data } = await api.post(`/api/auth/impersonate/${r.id}`);
      onImpersonate(data.token, {
        role: data.role,
        restaurant_id: data.restaurant_id,
        email: data.email,
      });
    } catch {
      const email = window.prompt(
        `У ресторана «${r.name}» нет владельца.\nВведите email для нового владельца:`
      );
      if (!email) return;
      const password = window.prompt('Введите пароль:');
      if (!password) return;
      try {
        await api.post('/api/auth/create-owner', {
          restaurant_id: r.id,
          email,
          password,
        });
        showMsg('✅ Владелец создан! Войдите снова.');
        fetchRestaurants();
      } catch (err2) {
        showMsg('❌ ' + (err2.response?.data?.error || 'Ошибка'));
      }
    }
  };

  return (
    <div style={s.page}>
      <div style={s.header}>
        <div>
          <div style={s.headerTitle}>⚡ Суперадмин</div>
          <div style={s.headerSub}>{user.email}</div>
        </div>
        <button onClick={onLogout} style={s.btnLogout}>
          Выйти
        </button>
      </div>

      {message && <div style={s.message}>{message}</div>}

      <div style={s.stats}>
        <div style={s.statCard}>
          <div style={s.statNum}>{restaurants.length}</div>
          <div style={s.statLabel}>Ресторанов</div>
        </div>
        <div style={s.statCard}>
          <div style={s.statNum}>{restaurants.filter((r) => r.status === 'active').length}</div>
          <div style={s.statLabel}>Активных</div>
        </div>
        <div style={s.statCard}>
          <div style={s.statNum}>
            {restaurants.reduce((sum, r) => sum + Number(r.items_count || 0), 0)}
          </div>
          <div style={s.statLabel}>Блюд всего</div>
        </div>
      </div>

      {!showCreate && !editItem && (
        <button style={s.btnCreate} onClick={() => setShowCreate(true)}>
          + Добавить ресторан
        </button>
      )}

      {showCreate && (
        <CreateRestaurantForm
          api={api}
          onSuccess={() => {
            setShowCreate(false);
            showMsg('✅ Ресторан создан!');
            fetchRestaurants();
          }}
          onCancel={() => setShowCreate(false)}
        />
      )}

      {editItem && (
        <EditRestaurantForm
          api={api}
          restaurant={editItem}
          onSuccess={() => {
            setEditItem(null);
            showMsg('✅ Сохранено!');
            fetchRestaurants();
          }}
          onCancel={() => setEditItem(null)}
        />
      )}

      {!showCreate && !editItem && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {restaurants.map((r) => (
            <div key={r.id} style={s.card}>
              <div style={s.cardTop}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={s.restName}>{r.name}</div>
                  <div style={s.restSlug}>/{r.slug}</div>
                  <div style={s.restMeta}>{r.owner_email || '—'}</div>
                  <div style={s.restMeta}>
                    🍕 {r.items_count ?? 0} блюд · 📂 {r.categories_count ?? 0} категорий · 📋 {r.orders_count ?? 0} заказов (30 дн.)
                  </div>
                  {r.trial_expires_at && (
                    <div style={s.restMeta}>
                      Триал до: {new Date(r.trial_expires_at).toLocaleDateString('ru-RU')}
                    </div>
                  )}
                </div>
                <div
                  style={{
                    ...s.badge,
                    backgroundColor: r.status === 'active' ? '#E8F5E9' : r.status === 'trial' ? '#FFF8E1' : '#FFEBEE',
                    color: r.status === 'active' ? '#2E7D32' : r.status === 'trial' ? '#F57F17' : '#E85D4A',
                  }}
                >
                  {r.status === 'active' ? '🟢 активен' : r.status === 'trial' ? '🟡 триал' : '🔴 заморожен'}
                </div>
              </div>

              <div style={s.cardActions}>
                <button style={s.btnAdmin} onClick={() => handleImpersonate(r)}>
                  Войти в админку
                </button>
                <button style={s.btnGreen} onClick={() => window.open(`/menu/${r.slug}`, '_blank')}>
                  Открыть меню
                </button>
                <button style={s.btnQR} onClick={() => setQrRestaurant(r)}>
                  QR-код
                </button>
                <button
                  style={{
                    ...s.btnQR,
                    backgroundColor:
                      r.plan === 'business' ? '#FFF8E1' :
                      r.plan === 'pro' ? '#F3E5F5' : '#E3F2FD',
                    color:
                      r.plan === 'business' ? '#F57F17' :
                      r.plan === 'pro' ? '#7B1FA2' : '#1565C0',
                  }}
                  onClick={() => setPlanRestaurant(r)}
                >
                  {r.plan === 'pro' ? '⚡ Pro' :
                   r.plan === 'business' ? '🚀 Business' : '📋 Basic'}
                </button>
              </div>
              <div style={{ ...s.cardActions, marginTop: '6px' }}>
                {r.status === 'trial' && (
                  <>
                    <button style={s.btnExtend} onClick={() => handleExtendTrial(r)}>
                      Продлить на 14 дней
                    </button>
                    <button style={s.btnActivate} onClick={() => handleActivate(r)}>
                      Активировать
                    </button>
                  </>
                )}
  {(r.status === 'active' || r.status === 'frozen') && (
    <button style={s.btnExtend} onClick={() => handleReturnToTrial(r)}>
      ↩️ Вернуть в триал
    </button>
  )}
              </div>
              <div style={{ ...s.cardActions, marginTop: '6px' }}>
                <button style={s.btnEdit} onClick={() => setEditItem(r)}>
                  Редактировать
                </button>
                <button style={s.btnToggle} onClick={() => handleToggleFrozen(r)}>
                  {r.status === 'frozen' ? 'Разморозить' : 'Заморозить'}
                </button>
                <button style={s.btnDanger} onClick={() => handleDelete(r)}>
                  Удалить
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {qrRestaurant && (
        <QRModal
          restaurant={qrRestaurant}
          onClose={() => setQrRestaurant(null)}
        />
      )}

      {planRestaurant && (
        <PlanSelector
          restaurant={planRestaurant}
          api={api}
          onSuccess={(msg) => {
            setPlanRestaurant(null);
            showMsg(msg);
            fetchRestaurants();
          }}
          onCancel={() => setPlanRestaurant(null)}
        />
      )}

      <BrandFooter />
    </div>
  );
}

function CreateRestaurantForm({ api, onSuccess, onCancel }) {
  const defaultTrial = () => {
    const d = new Date();
    d.setDate(d.getDate() + 14);
    return d.toISOString().slice(0, 10);
  };
  const [form, setForm] = useState({
    name: '',
    slug: '',
    email: '',
    password: '',
    phone: '',
    address: '',
    description: '',
    trial_expires_at: defaultTrial(),
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const autoSlug = (name) => {
    const slug = name
      .toLowerCase()
      .replace(/\s+/g, '-')
      .replace(/[^a-z0-9-]/g, '')
      .slice(0, 30);
    setForm((f) => ({ ...f, name, slug }));
  };

  const handleSubmit = async () => {
    if (!form.name || !form.slug || !form.email || !form.password) {
      setError('Заполните все обязательные поля');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const payload = { ...form };
      if (payload.trial_expires_at) {
        payload.trial_expires_at = payload.trial_expires_at + 'T23:59:59';
      } else {
        delete payload.trial_expires_at;
      }
      await api.post('/api/auth/restaurants', payload);
      onSuccess();
    } catch (err) {
      setError(err.response?.data?.error || 'Ошибка');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={s.formCard}>
      <div style={s.formTitle}>Новый ресторан</div>
      {error && <div style={s.error}>{error}</div>}
      {[
        { key: 'name', label: 'Название *', ph: 'Мой ресторан', onChange: autoSlug },
        { key: 'slug', label: 'URL slug *', ph: 'moy-restoran' },
        { key: 'email', label: 'Email владельца *', ph: 'owner@cafe.com' },
        { key: 'password', label: 'Пароль *', ph: 'Минимум 6 символов', type: 'password' },
        { key: 'trial_expires_at', label: 'Триал до', ph: '', type: 'date' },
        { key: 'phone', label: 'Телефон', ph: '+998 71 123 45 67' },
        { key: 'address', label: 'Адрес', ph: 'ул. Амира Темура, 15' },
        { key: 'description', label: 'Описание', ph: 'Уютное кафе...' },
      ].map(({ key, label, ph, type, onChange }) => (
        <div key={key} style={{ marginBottom: '12px' }}>
          <div style={s.label}>{label}</div>
          <input
            style={s.input}
            type={type || 'text'}
            value={form[key]}
            placeholder={ph}
            onChange={(e) =>
              onChange ? onChange(e.target.value) : setForm((f) => ({ ...f, [key]: e.target.value }))
            }
          />
        </div>
      ))}
      <div style={{ display: 'flex', gap: '8px' }}>
        <button style={{ ...s.btnCreate, flex: 1 }} onClick={handleSubmit} disabled={loading}>
          {loading ? 'Создание...' : 'Создать ресторан'}
        </button>
        <button style={s.btnCancel} onClick={onCancel}>
          Отмена
        </button>
      </div>
    </div>
  );
}

function EditRestaurantForm({ api, restaurant, onSuccess, onCancel }) {
  const [form, setForm] = useState({ ...restaurant });
  const [newPass, setNewPass] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSave = async () => {
    setLoading(true);
    setError('');
    try {
      await api.put(`/api/auth/restaurants/${restaurant.id}`, form);
      if (newPass.trim()) {
        await api.put(`/api/auth/restaurants/${restaurant.id}/password`, {
          password: newPass,
        });
      }
      onSuccess();
    } catch (err) {
      setError(err.response?.data?.error || 'Ошибка');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={s.formCard}>
      <div style={s.formTitle}>✏️ {restaurant.name}</div>
      {error && <div style={s.error}>{error}</div>}
      {[
        { key: 'name', label: 'Название' },
        { key: 'slug', label: 'URL slug' },
        { key: 'phone', label: 'Телефон' },
        { key: 'address', label: 'Адрес' },
        { key: 'description', label: 'Описание' },
      ].map(({ key, label }) => (
        <div key={key} style={{ marginBottom: '12px' }}>
          <div style={s.label}>{label}</div>
          <input
            style={s.input}
            value={form[key] || ''}
            onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
          />
        </div>
      ))}
      <div style={{ marginBottom: '12px' }}>
        <div style={s.label}>Новый пароль (оставьте пустым чтобы не менять)</div>
        <input
          style={s.input}
          type="password"
          value={newPass}
          placeholder="Новый пароль..."
          onChange={(e) => setNewPass(e.target.value)}
        />
      </div>
      <div style={{ display: 'flex', gap: '8px' }}>
        <button style={{ ...s.btnCreate, flex: 1 }} onClick={handleSave} disabled={loading}>
          {loading ? 'Сохранение...' : 'Сохранить'}
        </button>
        <button style={s.btnCancel} onClick={onCancel}>
          Отмена
        </button>
      </div>
    </div>
  );
}

const s = {
  page: {
    maxWidth: '800px',
    margin: '0 auto',
    padding: '16px 12px',
    fontFamily: '-apple-system, sans-serif',
    backgroundColor: '#f5f5f7',
    minHeight: '100vh',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '16px',
  },
  headerTitle: { fontSize: '20px', fontWeight: '700' },
  headerSub: { fontSize: '13px', color: '#888' },
  btnLogout: {
    padding: '8px 16px',
    borderRadius: '10px',
    border: 'none',
    backgroundColor: '#FFEBEE',
    color: '#E85D4A',
    cursor: 'pointer',
    fontSize: '14px',
  },
  message: {
    padding: '12px',
    borderRadius: '12px',
    backgroundColor: '#E8F5E9',
    marginBottom: '12px',
    textAlign: 'center',
    fontWeight: '500',
  },
  stats: { display: 'flex', gap: '10px', marginBottom: '16px' },
  statCard: {
    flex: 1,
    backgroundColor: '#fff',
    borderRadius: '16px',
    padding: '16px',
    textAlign: 'center',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
  },
  statNum: { fontSize: '28px', fontWeight: '700' },
  statLabel: { fontSize: '12px', color: '#888', marginTop: '4px' },
  btnCreate: {
    padding: '14px 20px',
    borderRadius: '12px',
    border: 'none',
    backgroundColor: '#000',
    color: '#fff',
    fontSize: '15px',
    fontWeight: '600',
    cursor: 'pointer',
    width: '100%',
    marginBottom: '12px',
    whiteSpace: 'nowrap',
  },
  card: {
    backgroundColor: '#fff',
    borderRadius: '16px',
    padding: '16px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
  },
  cardTop: {
    display: 'flex',
    gap: '12px',
    alignItems: 'flex-start',
    marginBottom: '12px',
  },
  restName: { fontSize: '16px', fontWeight: '600', marginBottom: '2px' },
  restSlug: { fontSize: '13px', color: '#888', marginBottom: '4px' },
  restMeta: { fontSize: '12px', color: '#aaa', marginBottom: '2px' },
  badge: {
    padding: '4px 10px',
    borderRadius: '100px',
    fontSize: '12px',
    fontWeight: '500',
    flexShrink: 0,
  },
  cardActions: { display: 'flex', gap: '8px', flexWrap: 'wrap' },
  btnAdmin: {
    padding: '8px 14px',
    borderRadius: '8px',
    border: 'none',
    backgroundColor: '#000',
    color: '#fff',
    cursor: 'pointer',
    fontSize: '13px',
    whiteSpace: 'nowrap',
  },
  btnGreen: {
    padding: '8px 14px',
    borderRadius: '8px',
    border: 'none',
    backgroundColor: '#E8F5E9',
    color: '#2E7D32',
    cursor: 'pointer',
    fontSize: '13px',
    whiteSpace: 'nowrap',
  },
  btnQR: {
    padding: '8px 14px',
    borderRadius: '8px',
    border: 'none',
    backgroundColor: '#F3E5F5',
    color: '#7B1FA2',
    cursor: 'pointer',
    fontSize: '13px',
    whiteSpace: 'nowrap',
  },
  btnEdit: {
    padding: '8px 14px',
    borderRadius: '8px',
    border: 'none',
    backgroundColor: '#F0F0F0',
    cursor: 'pointer',
    fontSize: '13px',
    whiteSpace: 'nowrap',
  },
  btnToggle: {
    padding: '8px 14px',
    borderRadius: '8px',
    border: 'none',
    backgroundColor: '#E3F2FD',
    color: '#1565C0',
    cursor: 'pointer',
    fontSize: '13px',
    whiteSpace: 'nowrap',
  },
  btnExtend: {
    padding: '8px 14px',
    borderRadius: '8px',
    border: 'none',
    backgroundColor: '#FFF8E1',
    color: '#F57F17',
    cursor: 'pointer',
    fontSize: '13px',
    whiteSpace: 'nowrap',
  },
  btnActivate: {
    padding: '8px 14px',
    borderRadius: '8px',
    border: 'none',
    backgroundColor: '#E8F5E9',
    color: '#2E7D32',
    cursor: 'pointer',
    fontSize: '13px',
    whiteSpace: 'nowrap',
  },
  btnDanger: {
    padding: '8px 14px',
    borderRadius: '8px',
    border: 'none',
    backgroundColor: '#FFEBEE',
    color: '#E85D4A',
    cursor: 'pointer',
    fontSize: '13px',
    whiteSpace: 'nowrap',
  },
  formCard: {
    backgroundColor: '#fff',
    borderRadius: '20px',
    padding: '20px',
    marginBottom: '16px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
  },
  formTitle: { fontSize: '18px', fontWeight: '700', marginBottom: '16px' },
  label: { fontSize: '13px', color: '#666', marginBottom: '4px', fontWeight: '500' },
  input: {
    width: '100%',
    padding: '10px 14px',
    borderRadius: '10px',
    border: '1px solid #E0E0E0',
    fontSize: '14px',
    boxSizing: 'border-box',
    outline: 'none',
  },
  btnCancel: {
    padding: '14px 16px',
    borderRadius: '12px',
    border: 'none',
    backgroundColor: '#F0F0F0',
    cursor: 'pointer',
    fontSize: '14px',
    whiteSpace: 'nowrap',
  },
  error: {
    padding: '10px',
    borderRadius: '10px',
    backgroundColor: '#FFEBEE',
    color: '#E85D4A',
    fontSize: '13px',
    marginBottom: '12px',
  },
};
